import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, Newspaper, Settings, ArrowRight, FolderOpen, Layers, 
  Mail, Eye, TrendingUp, Calendar, Clock, CheckCircle, AlertCircle,
  Users, Globe, Image, FileText, MousePointerClick, BarChart3
} from 'lucide-react';
import { brandsApi, meshNewsApi, companyApi, sectorsApi, contactApi, analyticsApi } from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    brands: [],
    news: [],
    sectors: [],
    contacts: [],
    company: null,
  });
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [brandsRes, newsRes, companyRes, sectorsRes, contactsRes, analyticsRes] = await Promise.all([
          brandsApi.getAll(),
          meshNewsApi.getAll(),
          companyApi.get(),
          sectorsApi.getAll(),
          contactApi.getSubmissions().catch(() => ({ data: [] })),
          analyticsApi.getStats().catch(() => ({ data: null })),
        ]);
        setStats({
          brands: brandsRes.data,
          news: newsRes.data,
          company: companyRes.data,
          sectors: sectorsRes.data,
          contacts: contactsRes.data || [],
        });
        setVisitorStats(analyticsRes.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount — API imports are stable module-level refs

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Calculate analytics
  const totalBrands = stats.brands.length;
  const totalNews = stats.news.length;
  const totalSectors = stats.sectors.length;
  const totalContacts = stats.contacts.length;
  const unreadContacts = stats.contacts.filter(c => !c.read).length;
  const pinnedNews = stats.news.filter(n => n.pinned).length;
  
  // Sectors with brands selected
  const sectorsWithBrands = stats.sectors.filter(s => s.selectedBrands?.length > 0).length;
  
  // News with images
  const newsWithImages = stats.news.filter(n => n.image).length;
  
  // Recent contacts (last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentContacts = stats.contacts.filter(c => {
    const date = new Date(c.submitted_at);
    return date >= sevenDaysAgo;
  });

  // News by month (last 6 months for chart)
  const getNewsByMonth = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const count = stats.news.filter(n => {
        const newsDate = new Date(n.date);
        return newsDate.getMonth() === date.getMonth() && 
               newsDate.getFullYear() === date.getFullYear();
      }).length;
      months.push({ month: monthName, count });
    }
    return months;
  };

  const newsByMonth = getNewsByMonth();
  const maxNewsCount = Math.max(...newsByMonth.map(m => m.count), 1);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Analytics Dashboard</h1>
        <span className="text-sm text-gray-500">
          <Clock className="h-4 w-4 inline mr-1" />
          Last updated: {new Date().toLocaleString()}
        </span>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Brands</p>
              <p className="text-3xl font-semibold text-gray-900">{totalBrands}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">News Articles</p>
              <p className="text-3xl font-semibold text-gray-900">{totalNews}</p>
              {pinnedNews > 0 && (
                <p className="text-xs text-amber-600 mt-1">{pinnedNews} pinned</p>
              )}
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Newspaper className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Sectors</p>
              <p className="text-3xl font-semibold text-gray-900">{totalSectors}</p>
              <p className="text-xs text-gray-500 mt-1">{sectorsWithBrands} with brands</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Layers className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Contact Forms</p>
              <p className="text-3xl font-semibold text-gray-900">{totalContacts}</p>
              {unreadContacts > 0 && (
                <p className="text-xs text-red-600 mt-1">{unreadContacts} unread</p>
              )}
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Mail className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Website Visitor Stats */}
      {visitorStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-sm p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">Page Views Today</p>
                <p className="text-3xl font-semibold">{visitorStats.todayViews || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Eye className="h-6 w-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg shadow-sm p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">Views This Week</p>
                <p className="text-3xl font-semibold">{visitorStats.weekViews || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <BarChart3 className="h-6 w-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg shadow-sm p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">Unique Visitors (Week)</p>
                <p className="text-3xl font-semibold">{visitorStats.uniqueVisitorsWeek || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Users className="h-6 w-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg shadow-sm p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">Total Page Views</p>
                <p className="text-3xl font-semibold">{visitorStats.totalViews || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <MousePointerClick className="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Analytics Row */}
      {visitorStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Views Chart */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <BarChart3 className="h-4 w-4 mr-2 text-gray-500" />
              Daily Page Views (Last 7 Days)
            </h3>
            {visitorStats.dailyViews?.length > 0 ? (
              <div className="flex items-end justify-between h-32 gap-2">
                {visitorStats.dailyViews.map((item, index) => {
                  const maxViews = Math.max(...visitorStats.dailyViews.map(d => d.views), 1);
                  const dayName = new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <div key={item.date} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-indigo-500 rounded-t transition-all hover:bg-indigo-600"
                        style={{ 
                          height: `${(item.views / maxViews) * 100}%`,
                          minHeight: item.views > 0 ? '8px' : '2px',
                          backgroundColor: item.views === 0 ? '#e5e7eb' : undefined
                        }}
                        title={`${item.views} views`}
                      />
                      <span className="text-xs text-gray-500 mt-2">{dayName}</span>
                      <span className="text-xs font-medium text-gray-700">{item.views}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
                No visitor data yet. Views will appear as visitors browse the site.
              </div>
            )}
          </div>

          {/* Top Pages */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <Globe className="h-4 w-4 mr-2 text-gray-500" />
              Top Pages (Last 7 Days)
            </h3>
            {visitorStats.topPages?.length > 0 ? (
              <div className="space-y-3">
                {visitorStats.topPages.slice(0, 5).map((page) => (
                  <div key={page.page} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 truncate flex-1 mr-4">
                      {page.page === '/' ? 'Home' : page.page}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{page.views} views</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
                No page view data yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* Charts and Details Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* News Activity Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2 text-gray-500" />
            News Activity (Last 6 Months)
          </h3>
          <div className="flex items-end justify-between h-32 gap-2">
            {newsByMonth.map((item) => (
              <div key={item.month} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                  style={{ 
                    height: `${(item.count / maxNewsCount) * 100}%`,
                    minHeight: item.count > 0 ? '8px' : '2px',
                    backgroundColor: item.count === 0 ? '#e5e7eb' : undefined
                  }}
                  title={`${item.count} articles`}
                />
                <span className="text-xs text-gray-500 mt-2">{item.month}</span>
                <span className="text-xs font-medium text-gray-700">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content Health */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircle className="h-4 w-4 mr-2 text-gray-500" />
            Content Health
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Sectors with brands</span>
                <span className="font-medium">{sectorsWithBrands}/{totalSectors}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${(sectorsWithBrands / totalSectors) * 100}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">News with images</span>
                <span className="font-medium">{newsWithImages}/{totalNews}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: totalNews > 0 ? `${(newsWithImages / totalNews) * 100}%` : '0%' }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Contact read rate</span>
                <span className="font-medium">{totalContacts - unreadContacts}/{totalContacts}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all"
                  style={{ width: totalContacts > 0 ? `${((totalContacts - unreadContacts) / totalContacts) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Contact Submissions */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <Mail className="h-4 w-4 mr-2 text-gray-500" />
              Recent Contact Submissions
            </h3>
            {recentContacts.length > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                {recentContacts.length} this week
              </span>
            )}
          </div>
          {stats.contacts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No contact submissions yet</p>
          ) : (
            <div className="space-y-3">
              {stats.contacts.slice(0, 5).map((contact, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${contact.read ? 'bg-gray-300' : 'bg-orange-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{contact.name}</p>
                    <p className="text-xs text-gray-500 truncate">{contact.subject}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(contact.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent News */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <Newspaper className="h-4 w-4 mr-2 text-gray-500" />
              Latest News Articles
            </h3>
            <Link to="/admin/news" className="text-xs text-blue-600 hover:underline">
              View all
            </Link>
          </div>
          {stats.news.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No news articles yet</p>
          ) : (
            <div className="space-y-3">
              {stats.news.slice(0, 5).map((article, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  {article.image ? (
                    <img 
                      src={article.image} 
                      alt="" 
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{article.title}</p>
                      {article.pinned && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Pinned</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(article.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Brand Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <Building2 className="h-4 w-4 mr-2 text-gray-500" />
            Brand Overview
          </h3>
          <Link to="/admin/brands" className="text-xs text-blue-600 hover:underline">
            Manage brands
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {stats.brands.map((brand) => (
            <Link
              key={brand.id}
              to={`/admin/brands/${brand.id}`}
              className="group text-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {brand.logo ? (
                <img 
                  src={brand.logo} 
                  alt={brand.name}
                  className="h-8 max-w-full object-contain mx-auto mb-2"
                />
              ) : (
                <div className="h-8 flex items-center justify-center mb-2">
                  <Building2 className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <p className="text-xs font-medium text-gray-900 truncate group-hover:text-blue-600">
                {brand.name}
              </p>
              <p className="text-xs text-gray-500">{brand.country}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link
          to="/admin/landing-page"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Globe className="h-5 w-5 text-gray-400 mr-3" />
              <span className="font-medium text-gray-900">Edit Landing Page</span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link
          to="/admin/sectors"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Layers className="h-5 w-5 text-gray-400 mr-3" />
              <span className="font-medium text-gray-900">Manage Sectors</span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link
          to="/admin/news"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Newspaper className="h-5 w-5 text-gray-400 mr-3" />
              <span className="font-medium text-gray-900">Add News Article</span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Eye className="h-5 w-5 text-gray-400 mr-3" />
              <span className="font-medium text-gray-900">View Website</span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </a>
      </div>
    </div>
  );
};

export default AdminDashboard;
